/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { updateModel } from "data/store/reducers/model";
import { useAuth } from "cms/auth/hooks/use-auth";
import { setChangeNeeded, setReloadRequired } from "data/store/reducers/webpage";
import { waitAtLeast } from "utils/sleep/wait-at-least";

/**
 * @typedef {Parameters<typeof updateModel>[0]["update"]} ModelUpdates
 */

const useUpdateModel = () => {
    
    const dispatch = useDispatch();
    const { token } = useAuth();

    const update = useCallback(
        /**
         * @param {ModelUpdates} update 
         * @param {boolean} reload 
         */
        async (update = {}, reload = false) => {
            
            // fixme: temporarily set change needed; this should be derived from state
            // and updated as the thunk passes through its states
            dispatch(setChangeNeeded(true));
            
            // this dispatch will return a promise, event though the types are incorrect
            /** @type {ReturnType<ReturnType<typeof updateModel>>} */
            const asyncAction = dispatch(
                updateModel({
                    token,
                    update
                })
            );

            const actionResult = await waitAtLeast(asyncAction, 150);

            if (reload) {
                dispatch(setReloadRequired(true));
            }

            return actionResult;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [token]
    );

    return update;
};

export { useUpdateModel };
